import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { RootState } from '@/store';
import { fetchProducts } from '@/store/slices/productsSlice';
import { fetchOrders } from '@/store/slices/ordersSlice';
import { Product, Order } from '@/types';
import { Button } from '@/components/ui/Button';
import { cn } from '@/utils/cn';
import { fadeInUp, staggerContainer, staggerItem } from '@/utils/animations';
import { getPrimaryImage } from '@/utils/imageUtils';
import { formatCurrency } from '@/utils/formatters';
import {
  ShoppingBagIcon,
  ShoppingCartIcon,
  TruckIcon,
  EyeIcon,
  HeartIcon,
  StarIcon,
  PlusIcon,
  ArrowRightIcon,
  BuildingStorefrontIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';

const BuyerDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const { products } = useSelector((state: RootState) => state.products);
  const { orders } = useSelector((state: RootState) => state.orders);

  const [recentProducts, setRecentProducts] = useState<Product[]>([]);
  const [recentOrders, setRecentOrders] = useState<Order[]>([]);

  useEffect(() => {
    // Fetch recent products and orders
    dispatch(fetchProducts());
    dispatch(fetchOrders());
  }, [dispatch]);

  useEffect(() => {
    // Get recent products (first 4)
    if (products.length > 0) {
      setRecentProducts(products.slice(0, 4));
    }
    
    // Get recent orders (first 3)
    if (orders.length > 0) {
      setRecentOrders(orders.slice(0, 3));
    }
  }, [products, orders]);

  const getOrderStatusColor = (status: string) => {
    switch (status) {
      case 'delivered':
        return 'bg-success-100 dark:bg-success-900/20 text-success-700 dark:text-success-400 border border-success-200 dark:border-success-800';
      case 'shipped':
        return 'bg-primary-100 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 border border-primary-200 dark:border-primary-800';
      case 'confirmed':
        return 'bg-info-100 dark:bg-info-900/20 text-info-700 dark:text-info-400 border border-info-200 dark:border-info-800';
      case 'cancelled':
        return 'bg-error-100 dark:bg-error-900/20 text-error-700 dark:text-error-400 border border-error-200 dark:border-error-800';
      default:
        return 'bg-warning-100 dark:bg-warning-900/20 text-warning-700 dark:text-warning-400 border border-warning-200 dark:border-warning-800';
    }
  };

  return (
    <motion.div initial="initial" animate="animate" variants={staggerContainer}>
      {/* Header */}
      <motion.div variants={staggerItem} className="mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white">
                Welcome back, {user?.first_name}!
              </h1>
              <SparklesIcon className="h-8 w-8 text-primary-500 dark:text-primary-400" />
            </div>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              Discover amazing products and track your orders
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              size="md"
              onClick={() => navigate('/products')}
              leftIcon={<EyeIcon className="h-5 w-5" />}
            >
              Browse Products
            </Button>
            <Button
              variant="outline"
              size="md"
              onClick={() => navigate('/seller')}
              leftIcon={<BuildingStorefrontIcon className="h-5 w-5" />}
            >
              Switch to Seller
            </Button>
            <Button
              size="md"
              onClick={() => navigate('/buyer/cart')}
              leftIcon={<ShoppingCartIcon className="h-5 w-5" />}
            >
              View Cart
            </Button>
          </div>
        </div>
      </motion.div>

      {/* Quick Stats */}
      <motion.div variants={staggerItem} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* Total Orders */}
        <div className="group bg-white dark:bg-dark-800 rounded-xl shadow-sm border border-gray-200 dark:border-dark-700 p-6 hover:shadow-lg transition-all duration-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Orders</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">{orders.length}</p>
            </div>
            <div className="p-3 bg-primary-100 dark:bg-primary-900/20 rounded-xl group-hover:scale-110 transition-transform duration-200">
              <ShoppingBagIcon className="h-7 w-7 text-primary-600 dark:text-primary-400" />
            </div>
          </div>
        </div>

        {/* Active Orders */}
        <div className="group bg-white dark:bg-dark-800 rounded-xl shadow-sm border border-gray-200 dark:border-dark-700 p-6 hover:shadow-lg transition-all duration-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Active Orders</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">
                {orders.filter(order => ['pending', 'confirmed', 'shipped'].includes(order.order_status)).length}
              </p>
            </div>
            <div className="p-3 bg-success-100 dark:bg-success-900/20 rounded-xl group-hover:scale-110 transition-transform duration-200">
              <TruckIcon className="h-7 w-7 text-success-600 dark:text-success-400" />
            </div>
          </div>
        </div>

        {/* Wishlist */}
        <div className="group bg-white dark:bg-dark-800 rounded-xl shadow-sm border border-gray-200 dark:border-dark-700 p-6 hover:shadow-lg transition-all duration-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Wishlist</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">0</p>
            </div>
            <div className="p-3 bg-error-100 dark:bg-error-900/20 rounded-xl group-hover:scale-110 transition-transform duration-200">
              <HeartIcon className="h-7 w-7 text-error-600 dark:text-error-400" />
            </div>
          </div>
        </div>

        {/* Reviews */}
        <div className="group bg-white dark:bg-dark-800 rounded-xl shadow-sm border border-gray-200 dark:border-dark-700 p-6 hover:shadow-lg transition-all duration-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Reviews</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">0</p>
            </div>
            <div className="p-3 bg-accent-100 dark:bg-accent-900/20 rounded-xl group-hover:scale-110 transition-transform duration-200">
              <StarIcon className="h-7 w-7 text-accent-600 dark:text-accent-400" />
            </div>
          </div>
        </div>
      </motion.div>

      <motion.div variants={staggerItem} className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Recent Orders */}
        <div className="bg-white dark:bg-dark-800 rounded-xl shadow-sm border border-gray-200 dark:border-dark-700 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Recent Orders</h2>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/buyer/orders')}
              rightIcon={<ArrowRightIcon className="h-4 w-4" />}
            >
              View All
            </Button>
          </div>
            
          {recentOrders.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-20 h-20 mx-auto mb-4 bg-gray-100 dark:bg-dark-700 rounded-full flex items-center justify-center">
                <ShoppingBagIcon className="h-10 w-10 text-gray-400 dark:text-gray-500" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No orders yet</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">Start shopping to see your orders here</p>
              <Button
                onClick={() => navigate('/products')}
                leftIcon={<PlusIcon className="h-5 w-5" />}
              >
                Start Shopping
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {recentOrders.map((order) => (
                <div
                  key={order.id}
                  className="flex items-center justify-between p-4 border border-gray-200 dark:border-dark-600 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors duration-200"
                >
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900 dark:text-white">{order.order_number}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {formatCurrency(Number(order.total_amount || 0))} • {new Date(order.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={cn('px-3 py-1 text-xs font-medium rounded-full', getOrderStatusColor(order.order_status))}>
                      {order.order_status}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/buyer/orders/${order.id}`)}
                    >
                      <EyeIcon className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Featured Products */}
        <div className="bg-white dark:bg-dark-800 rounded-xl shadow-sm border border-gray-200 dark:border-dark-700 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Featured Products</h2>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/products')}
              rightIcon={<ArrowRightIcon className="h-4 w-4" />}
            >
              View All
            </Button>
          </div>
            
          {recentProducts.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-20 h-20 mx-auto mb-4 bg-gray-100 dark:bg-dark-700 rounded-full flex items-center justify-center">
                <ShoppingBagIcon className="h-10 w-10 text-gray-400 dark:text-gray-500" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No products available</h3>
              <p className="text-gray-600 dark:text-gray-400">Check back later for new products</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3">
              {recentProducts.map((product) => (
                <div
                  key={product.id}
                  className="flex items-center gap-4 p-4 border border-gray-200 dark:border-dark-600 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700 cursor-pointer transition-colors duration-200"
                  onClick={() => navigate(`/products/${product.id}`)}
                >
                  <div className="flex-shrink-0">
                    <img
                      src={getPrimaryImage(product.images)}
                      alt={product.name}
                      className="w-16 h-16 object-cover rounded-lg"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 dark:text-white truncate">{product.name}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400 truncate">{product.description}</p>
                    <p className="text-lg font-bold text-primary-600 dark:text-primary-400 mt-1">
                      {formatCurrency(Number(product.price))}
                    </p>
                  </div>
                  <div className="flex-shrink-0">
                    <Button
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        console.log('Add to cart:', product.id);
                      }}
                    >
                      <PlusIcon className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </motion.div>

      {/* Quick Actions */}
      <motion.div variants={staggerItem} className="mt-8 bg-white dark:bg-dark-800 rounded-xl shadow-sm border border-gray-200 dark:border-dark-700 p-6">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Button
            variant="outline"
            size="lg"
            onClick={() => navigate('/products')}
            className="justify-start"
            leftIcon={<EyeIcon className="h-5 w-5" />}
          >
            Browse All Products
          </Button>
          <Button
            variant="outline"
            size="lg"
            onClick={() => navigate('/buyer/cart')}
            className="justify-start"
            leftIcon={<ShoppingCartIcon className="h-5 w-5" />}
          >
            View Shopping Cart
          </Button>
          <Button
            variant="outline"
            size="lg"
            onClick={() => navigate('/buyer/orders')}
            className="justify-start"
            leftIcon={<TruckIcon className="h-5 w-5" />}
          >
            Track Orders
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default BuyerDashboard;
